import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import core, { App } from '@gin-betting/core';
import filters from '@gin-betting/filters';
import { createSelector } from 'reselect';

import { useAppliedFilters, useMemoSelector, useWithLocation } from 'hooks';
import ui from 'modules/ui';
import type { State } from 'types';
import { checkIsDynamicCategorizer } from 'utils';
import { OverlayType } from '../../constants';
import {
    getCategorizerVars,
    getFormattedCountryCode,
    getSelectedCountryKeyInfo,
} from '../../utils';
import { MobileFiltersCtx } from './MobileFiltersCtx';

interface Output {
    selectedCountryKey: string;
    countryCode: string | null | undefined;
    isSelectedCountryCodeWithAll: boolean;
    handleClickBack: VoidFn;
    tournamentName: string | undefined;
    tournamentId: string | undefined;
    currentOverlay: OverlayType;
    withSportNavigation: boolean;
}

const {
    entities: {
        selectors: { selectTournaments },
    },
    poller: { usePoller },
    utils: { setSearchString },
    hooks: { useDeepMemo },
} = core;

const {
    actions: { getCategorizer },
} = filters;

const {
    actions: { openFilter, closeFilter, setActiveMobileCountryKey },
    selectors: { selectActiveMobileCountryKey },
} = ui;

function useMobileFilters(): Output {
    const { currentOverlay, setOverlay } = useContext(MobileFiltersCtx);
    const dispatch = useDispatch();
    const currentFilters = useAppliedFilters();
    const { vars, withSportNavigation } = useWithLocation(() => ({
        withSportNavigation:
            !checkIsDynamicCategorizer() &&
            currentOverlay !== OverlayType.FEATURED_TOURNAMENTS_LIST,
        vars: getCategorizerVars(),
    }));
    const variables = useDeepMemo(vars);

    const { tournamentId, tournamentIds } = currentFilters;

    const { tournamentName, countryCode, activeCountryKey } = useMemoSelector(
        selectProps,
        {
            tournamentId: tournamentId || tournamentIds?.[0],
        }
    );

    const { requestContent } = usePoller({
        pollerName: App.PollerName.Categorizer,
        executableAction: getCategorizer,
        variables,
        skipInitialRequest: true,
    });

    const {
        selectedCountryKey,
        isSelectedCountryCodeWithAll,
    } = getSelectedCountryKeyInfo(activeCountryKey);

    const handleClickBack = () => {
        if (isSelectedCountryCodeWithAll) {
            dispatch(setActiveMobileCountryKey(selectedCountryKey));

            setSearchString({
                ...currentFilters,
                tournamentId: undefined,
                tournamentIds: tournamentIds?.[0]
                    ? [tournamentIds?.[0]]
                    : undefined,
            });

            setOverlay(OverlayType.TOURNAMENT_LIST);

            return;
        }

        if (!tournamentId) {
            dispatch(setActiveMobileCountryKey(''));
            setOverlay(OverlayType.TOURNAMENT_LIST);

            return;
        }

        if (countryCode) {
            dispatch(setActiveMobileCountryKey(countryCode));
        }

        setSearchString({
            ...currentFilters,
            tournamentId: undefined,
            tournamentIds: countryCode ? [tournamentId] : undefined,
        });
    };

    useEffect(() => {
        dispatch(
            currentOverlay === OverlayType.NONE ? closeFilter() : openFilter()
        );
    }, [currentOverlay, dispatch]);

    useEffect(() => {
        requestContent();
    }, [variables, requestContent]);

    return {
        currentOverlay,
        selectedCountryKey,
        isSelectedCountryCodeWithAll,
        handleClickBack,
        countryCode,
        tournamentName,
        tournamentId: tournamentId || tournamentIds?.[0],
        withSportNavigation,
    };
}

const selectTournamentIdFromProps = <T extends { tournamentId: string }>(
    _state: State,
    props: T
): string => props.tournamentId;

const selectTournament = createSelector(
    selectTournamentIdFromProps,
    selectTournaments,
    (tournamentId, tournaments) => {
        if (!tournamentId) return;

        return tournaments.get(tournamentId);
    }
);

const selectProps = createSelector(
    selectTournament,
    selectActiveMobileCountryKey,
    (tournament, activeCountryKey) => ({
        tournamentName: tournament?.name,
        countryCode: tournament?.countryCode
            ? getFormattedCountryCode(tournament?.countryCode)
            : tournament?.countryCode,
        activeCountryKey,
    })
);

export default useMobileFilters;
